import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { Slider } from '../components/ui/slider';
import { 
  UserPlus, Clock, CheckCircle, XCircle, AlertTriangle, TrendingUp,
  FileText, Users, Building2, Phone, Calendar, Activity, Brain,
  ClipboardCheck, ArrowRight, RefreshCw, Search, Filter, Bell,
  Stethoscope, Heart, Shield, DollarSign, BarChart3, PieChart,
  Timer, Target, ChevronRight, Zap, Eye, Edit, Trash2, Upload,
  FileSearch, AlertOctagon, Copy, Printer, Download, Pill, 
  CalendarClock, GitBranch, LinkIcon, Webhook, Play, Pause, Clipboard
} from 'lucide-react';
import { toast } from 'sonner';
import AdmissionPlanOfCare from '../components/AdmissionPlanOfCare';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdmissionsDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [dashboard, setDashboard] = useState(null);
  const [pipeline, setPipeline] = useState(null);
  const [slaAlerts, setSlaAlerts] = useState([]);
  const [conversionAnalytics, setConversionAnalytics] = useState(null);
  const [leakageAlerts, setLeakageAlerts] = useState(null);
  const [lcdCategories, setLcdCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewReferralDialog, setShowNewReferralDialog] = useState(false);
  const [showLcdExtractionDialog, setShowLcdExtractionDialog] = useState(false);
  const [lcdExtractionResult, setLcdExtractionResult] = useState(null);
  const [physicianWriteup, setPhysicianWriteup] = useState(null);
  const [lcdLoading, setLcdLoading] = useState(false);
  const [lcdForm, setLcdForm] = useState({
    referral_id: '',
    record_type: 'discharge_summary',
    record_text: '',
    facility_name: ''
  });
  const [showReferralDetails, setShowReferralDetails] = useState(false);
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [conversionScore, setConversionScore] = useState(null);
  const [readinessChecklist, setReadinessChecklist] = useState(null);
  const [physicianSummary, setPhysicianSummary] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [referralForm, setReferralForm] = useState({
    referral_source: 'hospital',
    referral_source_name: '',
    referral_source_contact: '',
    patient_first_name: '',
    patient_last_name: '',
    patient_dob: '',
    patient_gender: 'male',
    patient_phone: '',
    patient_address: '',
    patient_city: '',
    patient_state: '',
    patient_zip: '',
    care_location: 'home',
    payer_type: 'medicare',
    medicare_number: '',
    medicaid_number: '',
    primary_diagnosis: '',
    primary_icd10: '',
    secondary_diagnoses: '',  // Will be converted to array on submit
    prognosis: 'less_than_6_months',
    attending_physician_name: '',
    attending_physician_npi: '',
    attending_physician_phone: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    emergency_contact_relationship: '',
    referral_reason: '',
    urgency: 'routine',
    additional_notes: ''
  });

  // HOPE Assessment State
  const [showHopeDialog, setShowHopeDialog] = useState(false);
  const [hopeLoading, setHopeLoading] = useState(false);
  const [hopeResult, setHopeResult] = useState(null);
  const [hopeForm, setHopeForm] = useState({
    referral_id: '',
    assessment_type: 'admission',
    assessment_date: new Date().toISOString().split('T')[0],
    assessor_name: '',
    assessor_npi: '',
    admission_date: new Date().toISOString().split('T')[0],
    reason_for_assessment: '01',
    pain_interview_conducted: '1',
    pain_presence: '1',
    pain_severity: 5,
    dyspnea_presence: '1',
    dyspnea_severity: '1',
    dyspnea_treatment: '1',
    nausea_presence: '1',
    nausea_severity: '1',
    anxiety_severity: '1',
    sadness_severity: '1',
    adl_score: 2,
    mobility_score: 2,
    cognition_score: 1,
    prognosis_indicator: 'stable',
    notes: ''
  });

  // Post-Admission Timeline State
  const [admissionTimelines, setAdmissionTimelines] = useState([]);
  const [selectedTimeline, setSelectedTimeline] = useState(null);
  const [timelineLoading, setTimelineLoading] = useState(false);

  // Unified Dashboard State (Single Source of Truth)
  const [unifiedDashboard, setUnifiedDashboard] = useState(null);
  const [docMonitor, setDocMonitor] = useState(null);
  const [reportingAnalytics, setReportingAnalytics] = useState(null);
  const [governanceCheck, setGovernanceCheck] = useState(null);

  // Referral Integrations State
  const [integrationSources, setIntegrationSources] = useState([
    { id: 'aidin', name: 'Aidin', status: 'connected', referrals_today: 3, icon: '🏥' },
    { id: 'careport', name: 'CarePort', status: 'connected', referrals_today: 5, icon: '🔗' },
    { id: 'epic', name: 'Epic', status: 'pending', referrals_today: 0, icon: '📋' }
  ]);
  const [recentIntegrationReferrals, setRecentIntegrationReferrals] = useState([]);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('hospice_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  }, []);

  // Fetch Unified Dashboard (Single Source of Truth)
  const fetchUnifiedDashboard = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admissions/unified-dashboard`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setUnifiedDashboard(data);
      }
    } catch (error) {
      console.error('Failed to fetch unified dashboard:', error);
    }
  }, [getAuthHeaders]);

  // Fetch Documentation Monitor for specific referral
  const fetchDocMonitor = useCallback(async (referralId) => {
    try {
      const response = await fetch(`${API_URL}/api/admissions/documentation-monitor/${referralId}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setDocMonitor(data);
      }
    } catch (error) {
      console.error('Failed to fetch doc monitor:', error);
    }
  }, [getAuthHeaders]);

  // Fetch Reporting Analytics
  const fetchReportingAnalytics = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admissions/analytics/reporting`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setReportingAnalytics(data);
      }
    } catch (error) {
      console.error('Failed to fetch reporting analytics:', error);
    }
  }, [getAuthHeaders]);

  // Validate governance before admission
  const validateGovernance = useCallback(async (referralId) => {
    try {
      const response = await fetch(`${API_URL}/api/admissions/governance/validate-admission/${referralId}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setGovernanceCheck(data);
        return data;
      }
    } catch (error) {
      console.error('Failed to validate governance:', error);
    }
    return null;
  }, [getAuthHeaders]);

  const fetchDashboard = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admissions/dashboard`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setDashboard(data);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    }
  }, [getAuthHeaders]);

  const fetchPipeline = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admissions/dashboard/pipeline`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setPipeline(data.pipeline);
      }
    } catch (error) {
      console.error('Failed to fetch pipeline:', error);
    }
  }, [getAuthHeaders]);

  const fetchSlaAlerts = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admissions/alerts/sla-breaches`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setSlaAlerts(data.alerts || []);
      }
    } catch (error) {
      console.error('Failed to fetch SLA alerts:', error);
    }
  }, [getAuthHeaders]);

  const fetchConversionAnalytics = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admissions/analytics/conversion`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setConversionAnalytics(data);
      }
    } catch (error) {
      console.error('Failed to fetch conversion analytics:', error);
    }
  }, [getAuthHeaders]);

  const fetchLeakageAlerts = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admissions/leakage-alerts`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setLeakageAlerts(data);
      }
    } catch (error) {
      console.error('Failed to fetch leakage alerts:', error);
    }
  }, [getAuthHeaders]);

  const fetchLcdCategories = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admissions/lcd-criteria`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setLcdCategories(data.categories || []);
      }
    } catch (error) {
      console.error('Failed to fetch LCD categories:', error);
    }
  }, [getAuthHeaders]);

  const handleLcdExtraction = async () => {
    if (!lcdForm.referral_id || !lcdForm.record_text) {
      toast.error('Please select a referral and enter hospital record text');
      return;
    }
    
    setLcdLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admissions/lcd-extraction/analyze`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(lcdForm)
      });
      
      if (response.ok) {
        const data = await response.json();
        setLcdExtractionResult(data.analysis);
        toast.success(`LCD extraction complete: ${data.analysis.eligibility_confidence}% confidence`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'LCD extraction failed');
      }
    } catch (error) {
      toast.error('Network error during LCD extraction');
    }
    setLcdLoading(false);
  };

  const handleGenerateWriteup = async (referralId) => {
    setLcdLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admissions/lcd-extraction/physician-writeup/${referralId}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setPhysicianWriteup(data.writeup);
        toast.success('Physician write-up generated');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to generate write-up');
      }
    } catch (error) {
      toast.error('Network error generating write-up');
    }
    setLcdLoading(false);
  };

  const acknowledgeLeakageAlert = async (referralId, actionTaken) => {
    try {
      const formData = new FormData();
      formData.append('action_taken', actionTaken);
      
      const response = await fetch(`${API_URL}/api/admissions/leakage-alerts/${referralId}/acknowledge`, {
        method: 'POST',
        headers: { 'Authorization': getAuthHeaders()['Authorization'] },
        body: formData
      });
      
      if (response.ok) {
        toast.success('Alert acknowledged');
        fetchLeakageAlerts();
      }
    } catch (error) {
      toast.error('Failed to acknowledge alert');
    }
  };

  const fetchReferralDetails = async (referralId) => {
    setDetailsLoading(true);
    try {
      // Fetch conversion score, readiness checklist, and physician summary in parallel
      const [scoreRes, readinessRes, summaryRes] = await Promise.all([
        fetch(`${API_URL}/api/admissions/conversion-score/${referralId}`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/admissions/readiness-checklist/${referralId}`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/admissions/physician-summary/${referralId}`, { headers: getAuthHeaders() })
      ]);
      
      if (scoreRes.ok) {
        setConversionScore(await scoreRes.json());
      }
      if (readinessRes.ok) {
        setReadinessChecklist(await readinessRes.json());
      }
      if (summaryRes.ok) {
        setPhysicianSummary(await summaryRes.json());
      }
    } catch (error) {
      console.error('Failed to fetch referral details:', error);
      toast.error('Failed to load referral details');
    }
    setDetailsLoading(false);
  };

  const openReferralDetails = (referral) => {
    setSelectedReferral(referral);
    setShowReferralDetails(true);
    fetchReferralDetails(referral.id);
  };

  // HOPE Assessment Functions
  const handleHopeAssessment = async () => {
    if (!hopeForm.referral_id) {
      toast.error('Please select a referral');
      return;
    }
    
    setHopeLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admissions/hope-assessment`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(hopeForm)
      });
      
      if (response.ok) {
        const data = await response.json();
        setHopeResult(data);
        if (data.sfv_triggered) {
          toast.warning(`HOPE Assessment complete - SFV TRIGGERED for ${data.sfv_symptoms?.length || 0} symptoms`, {
            duration: 5000
          });
        } else {
          toast.success('HOPE Assessment completed successfully');
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'HOPE assessment failed');
      }
    } catch (error) {
      toast.error('Network error during HOPE assessment');
    }
    setHopeLoading(false);
  };

  const exportHopeToCMS = async (hopeId) => {
    try {
      const response = await fetch(`${API_URL}/api/admissions/hope-assessment/${hopeId}/cms-export`, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        // Create downloadable file
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `HOPE_CMS_Export_${hopeId}.json`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success('CMS HOPE export downloaded');
      }
    } catch (error) {
      toast.error('Failed to export HOPE data');
    }
  };

  // Timeline Functions
  const fetchAdmissionTimeline = async (referralId) => {
    setTimelineLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admissions/admission-timeline/${referralId}`, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setSelectedTimeline(data);
      } else if (response.status === 404) {
        // Timeline doesn't exist, create one
        const createRes = await fetch(`${API_URL}/api/admissions/admission-timeline/create/${referralId}`, {
          method: 'POST',
          headers: getAuthHeaders()
        });
        if (createRes.ok) {
          const data = await createRes.json();
          setSelectedTimeline(data.timeline);
          toast.success('Admission timeline created');
        }
      }
    } catch (error) {
      console.error('Failed to fetch timeline:', error);
    }
    setTimelineLoading(false);
  };

  const updateTimelineItem = async (referralId, milestoneId, task, status) => {
    try {
      const response = await fetch(`${API_URL}/api/admissions/admission-timeline/${referralId}/milestone/${milestoneId}/item?task=${encodeURIComponent(task)}&status=${status}`, {
        method: 'PATCH',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        toast.success(`Task "${task}" marked as ${status}`);
        fetchAdmissionTimeline(referralId);
      }
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  // Medication & Frequency Order Functions
  const createMedicationOrders = async (referralId, medications) => {
    try {
      const response = await fetch(`${API_URL}/api/admissions/medication-orders/${referralId}`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(medications)
      });
      
      if (response.ok) {
        toast.success('Medication orders created');
        fetchAdmissionTimeline(referralId);
      }
    } catch (error) {
      toast.error('Failed to create medication orders');
    }
  };

  const createFrequencyOrders = async (referralId, frequencies) => {
    try {
      const response = await fetch(`${API_URL}/api/admissions/frequency-orders/${referralId}`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(frequencies)
      });
      
      if (response.ok) {
        toast.success('Frequency orders created');
        fetchAdmissionTimeline(referralId);
      }
    } catch (error) {
      toast.error('Failed to create frequency orders');
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchDashboard(), 
        fetchPipeline(), 
        fetchSlaAlerts(), 
        fetchConversionAnalytics(),
        fetchLeakageAlerts(),
        fetchLcdCategories(),
        fetchUnifiedDashboard(),
        fetchReportingAnalytics()
      ]);
      setLoading(false);
    };
    loadData();
    
    // Refresh every 30 seconds
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [fetchDashboard, fetchPipeline, fetchSlaAlerts, fetchConversionAnalytics, fetchLeakageAlerts, fetchLcdCategories, fetchUnifiedDashboard, fetchReportingAnalytics]);

  const handleCreateReferral = async () => {
    try {
      // Prepare form data with correct types for backend
      const payload = {
        ...referralForm,
        // Convert secondary_diagnoses from string to array
        secondary_diagnoses: referralForm.secondary_diagnoses 
          ? referralForm.secondary_diagnoses.split(',').map(d => d.trim()).filter(Boolean)
          : [],
        // Ensure optional fields are null instead of empty strings for cleaner data
        referral_source_name: referralForm.referral_source_name || null,
        referral_source_contact: referralForm.referral_source_contact || null,
        patient_ssn_last4: referralForm.patient_ssn_last4 || null,
        patient_phone: referralForm.patient_phone || null,
        patient_address: referralForm.patient_address || null,
        patient_city: referralForm.patient_city || null,
        patient_state: referralForm.patient_state || null,
        patient_zip: referralForm.patient_zip || null,
        facility_name: referralForm.facility_name || null,
        medicare_number: referralForm.medicare_number || null,
        medicaid_number: referralForm.medicaid_number || null,
        insurance_name: referralForm.insurance_name || null,
        insurance_id: referralForm.insurance_id || null,
        primary_diagnosis_icd10: referralForm.primary_icd10 || null,
        attending_physician_name: referralForm.attending_physician_name || null,
        attending_physician_npi: referralForm.attending_physician_npi || null,
        attending_physician_phone: referralForm.attending_physician_phone || null,
        emergency_contact_name: referralForm.emergency_contact_name || null,
        emergency_contact_phone: referralForm.emergency_contact_phone || null,
        emergency_contact_relationship: referralForm.emergency_contact_relationship || null,
        referral_reason: referralForm.referral_reason || null,
        additional_notes: referralForm.additional_notes || null
      };
      
      // Remove the frontend-only field
      delete payload.primary_icd10;
      
      const response = await fetch(`${API_URL}/api/admissions/referral/intake`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Referral ${data.referral_id} created successfully`);
        setShowNewReferralDialog(false);
        setReferralForm({
          referral_source: 'hospital',
          referral_source_name: '',
          patient_first_name: '',
          patient_last_name: '',
          patient_dob: '',
          patient_gender: 'male',
          patient_phone: '',
          care_location: 'home',
          payer_type: 'medicare',
          primary_diagnosis: '',
          primary_icd10: '',
          secondary_diagnoses: '',
          prognosis: 'less_than_6_months',
          attending_physician_name: '',
          urgency: 'routine'
        });
        fetchDashboard();
        fetchPipeline();
      } else {
        const error = await response.json();
        const errorMsg = Array.isArray(error.detail) 
          ? error.detail.map(e => e.msg).join(', ')
          : error.detail || 'Failed to create referral';
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error('Referral creation error:', error);
      toast.error('Network error creating referral. Please try again.');
    }
  };

  const handleRunEligibility = async (referralId) => {
    try {
      const response = await fetch(`${API_URL}/api/admissions/eligibility/ai-screen/${referralId}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Eligibility: ${data.eligibility_status} (${Math.round(data.assessment.confidence * 100)}% confidence)`);
        fetchDashboard();
        fetchPipeline();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to run eligibility');
      }
    } catch (error) {
      toast.error('Network error running eligibility');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      new: 'bg-blue-500',
      pending_eligibility: 'bg-yellow-500',
      eligible: 'bg-green-500',
      potentially_eligible: 'bg-amber-500',
      not_eligible: 'bg-red-500',
      pending_decision: 'bg-purple-500',
      in_admission: 'bg-indigo-500',
      admitted: 'bg-emerald-500',
      not_admitted: 'bg-gray-500'
    };
    return colors[status] || 'bg-gray-400';
  };

  const formatStatusLabel = (status) => {
    return status?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Unknown';
  };

  if (loading && !dashboard) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-indigo-600 mx-auto mb-4" />
          <p className="text-slate-600">Loading Admissions Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-6" data-testid="admissions-dashboard">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <UserPlus className="w-7 h-7 text-indigo-600" />
            Admissions Workflow
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            End-to-End Hospice Admission Orchestration
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => { fetchDashboard(); fetchPipeline(); fetchSlaAlerts(); }}
            data-testid="refresh-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Dialog open={showNewReferralDialog} onOpenChange={setShowNewReferralDialog}>
            <DialogTrigger asChild>
              <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="new-referral-btn">
                <UserPlus className="w-4 h-4 mr-2" />
                New Referral
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-xl font-bold">Create New Referral</DialogTitle>
                <p className="text-sm text-gray-500 mt-1">Fields marked with * are required</p>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-4 py-4">
                {/* Referral Source */}
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-gray-900">Referral Source *</Label>
                  <Select 
                    value={referralForm.referral_source}
                    onValueChange={(v) => setReferralForm(prev => ({...prev, referral_source: v}))}
                  >
                    <SelectTrigger className="bg-white border-gray-300">
                      <SelectValue placeholder="Select source..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hospital">Hospital</SelectItem>
                      <SelectItem value="snf">SNF</SelectItem>
                      <SelectItem value="physician">Physician</SelectItem>
                      <SelectItem value="family">Family</SelectItem>
                      <SelectItem value="alf">ALF</SelectItem>
                      <SelectItem value="home_health">Home Health</SelectItem>
                      <SelectItem value="self">Self</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Urgency */}
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-gray-900">Urgency</Label>
                  <Select 
                    value={referralForm.urgency}
                    onValueChange={(v) => setReferralForm(prev => ({...prev, urgency: v}))}
                  >
                    <SelectTrigger className="bg-white border-gray-300">
                      <SelectValue placeholder="Select urgency..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="routine">Routine</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                      <SelectItem value="emergent">Emergent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Patient Info */}
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-gray-900">First Name *</Label>
                  <Input 
                    value={referralForm.patient_first_name}
                    onChange={(e) => setReferralForm(prev => ({...prev, patient_first_name: e.target.value}))}
                    placeholder="Enter patient first name"
                    className="bg-white border-gray-300"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-gray-900">Last Name *</Label>
                  <Input 
                    value={referralForm.patient_last_name}
                    onChange={(e) => setReferralForm(prev => ({...prev, patient_last_name: e.target.value}))}
                    placeholder="Enter patient last name"
                    className="bg-white border-gray-300"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-gray-900">Date of Birth *</Label>
                  <Input 
                    type="date"
                    value={referralForm.patient_dob}
                    onChange={(e) => setReferralForm(prev => ({...prev, patient_dob: e.target.value}))}
                    className="bg-white border-gray-300"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-gray-900">Gender *</Label>
                  <Select 
                    value={referralForm.patient_gender}
                    onValueChange={(v) => setReferralForm(prev => ({...prev, patient_gender: v}))}
                  >
                    <SelectTrigger className="bg-white border-gray-300">
                      <SelectValue placeholder="Select gender..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Location & Payer */}
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-gray-900">Care Location *</Label>
                  <Select 
                    value={referralForm.care_location}
                    onValueChange={(v) => setReferralForm(prev => ({...prev, care_location: v}))}
                  >
                    <SelectTrigger className="bg-white border-gray-300">
                      <SelectValue placeholder="Select location..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="home">Home</SelectItem>
                      <SelectItem value="alf">ALF</SelectItem>
                      <SelectItem value="snf">SNF</SelectItem>
                      <SelectItem value="hospital">Hospital</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-gray-900">Payer Type *</Label>
                  <Select 
                    value={referralForm.payer_type}
                    onValueChange={(v) => setReferralForm(prev => ({...prev, payer_type: v}))}
                  >
                    <SelectTrigger className="bg-white border-gray-300">
                      <SelectValue placeholder="Select payer..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="medicare">Medicare</SelectItem>
                      <SelectItem value="medicaid">Medicaid</SelectItem>
                      <SelectItem value="commercial">Commercial</SelectItem>
                      <SelectItem value="dual">Dual (Medicare/Medicaid)</SelectItem>
                      <SelectItem value="va">VA</SelectItem>
                      <SelectItem value="self_pay">Self Pay</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Clinical */}
                <div className="col-span-2 space-y-2">
                  <Label className="text-sm font-semibold text-gray-900">Primary Diagnosis *</Label>
                  <Input 
                    value={referralForm.primary_diagnosis}
                    onChange={(e) => setReferralForm(prev => ({...prev, primary_diagnosis: e.target.value}))}
                    placeholder="e.g., Metastatic lung cancer, End-stage CHF"
                    className="bg-white border-gray-300"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-gray-900">Prognosis *</Label>
                  <Select 
                    value={referralForm.prognosis}
                    onValueChange={(v) => setReferralForm(prev => ({...prev, prognosis: v}))}
                  >
                    <SelectTrigger className="bg-white border-gray-300">
                      <SelectValue placeholder="Select prognosis..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="less_than_6_months">Less than 6 months</SelectItem>
                      <SelectItem value="uncertain">Uncertain</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-gray-900">Attending Physician</Label>
                  <Input 
                    value={referralForm.attending_physician_name}
                    onChange={(e) => setReferralForm(prev => ({...prev, attending_physician_name: e.target.value}))}
                    placeholder="Dr. Smith"
                    className="bg-white border-gray-300"
                  />
                </div>
                
                {/* Important Notice */}
                <div className="col-span-2 p-4 bg-blue-50 border border-blue-200 rounded-lg mt-2">
                  <h4 className="text-sm font-bold text-blue-800 mb-2">📋 Admission Requirements</h4>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>• <strong>Election of Benefit</strong> - Required before admission (patient/family signature)</li>
                    <li>• <strong>CTI</strong> - Verbal certification OK at admission, written within 48 hrs of SOC</li>
                    <li>• <strong>HOPE Assessment</strong> - Must be completed within 48 hrs of Start of Care</li>
                  </ul>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowNewReferralDialog(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleCreateReferral}
                  disabled={!referralForm.patient_first_name || !referralForm.patient_last_name || !referralForm.primary_diagnosis}
                  className="bg-indigo-600 hover:bg-indigo-700"
                >
                  Create Referral
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* SLA Alerts Banner */}
      {slaAlerts.length > 0 && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-red-700 font-medium mb-2">
            <Bell className="w-5 h-5" />
            SLA Breach Alerts ({slaAlerts.length})
          </div>
          <div className="space-y-2">
            {slaAlerts.slice(0, 3).map((alert, idx) => (
              <div key={idx} className="flex items-center justify-between text-sm">
                <span className="text-red-600">{alert.message}</span>
                <Badge variant={alert.severity === 'critical' ? 'destructive' : 'secondary'}>
                  {alert.severity}
                </Badge>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-xs">Total Referrals</p>
                <p className="text-2xl font-bold">{dashboard?.summary?.total_referrals || 0}</p>
              </div>
              <FileText className="w-8 h-8 text-blue-200" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-amber-100 text-xs">Pending Eligibility</p>
                <p className="text-2xl font-bold">{dashboard?.summary?.pending_eligibility || 0}</p>
              </div>
              <Clock className="w-8 h-8 text-amber-200" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-xs">Pending Decision</p>
                <p className="text-2xl font-bold">{dashboard?.summary?.pending_decision || 0}</p>
              </div>
              <Users className="w-8 h-8 text-purple-200" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-indigo-500 to-indigo-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-indigo-100 text-xs">In Admission</p>
                <p className="text-2xl font-bold">{dashboard?.summary?.in_admission || 0}</p>
              </div>
              <Activity className="w-8 h-8 text-indigo-200" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-emerald-100 text-xs">Admitted</p>
                <p className="text-2xl font-bold">{dashboard?.summary?.admitted || 0}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-emerald-200" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-rose-500 to-rose-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-rose-100 text-xs">Not Admitted</p>
                <p className="text-2xl font-bold">{dashboard?.summary?.not_admitted || 0}</p>
              </div>
              <XCircle className="w-8 h-8 text-rose-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-white border flex-wrap">
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="pipeline" className="flex items-center gap-2">
            <Activity className="w-4 h-4" />
            Pipeline
          </TabsTrigger>
          <TabsTrigger value="documentation" className="flex items-center gap-2 relative">
            <FileText className="w-4 h-4" />
            Doc Monitor
            {unifiedDashboard?.documentation_stats && 
             (unifiedDashboard.summary?.in_process - unifiedDashboard.documentation_stats.consents_complete) > 0 && (
              <Badge className="ml-1 bg-amber-500 text-white text-xs">
                {unifiedDashboard.summary?.in_process - unifiedDashboard.documentation_stats.consents_complete}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="hope" className="flex items-center gap-2">
            <ClipboardCheck className="w-4 h-4" />
            HOPE Assessment
          </TabsTrigger>
          <TabsTrigger value="timeline" className="flex items-center gap-2">
            <CalendarClock className="w-4 h-4" />
            Post-Admission
          </TabsTrigger>
          <TabsTrigger value="analytics" className="flex items-center gap-2">
            <PieChart className="w-4 h-4" />
            Analytics
          </TabsTrigger>
          <TabsTrigger value="integrations" className="flex items-center gap-2">
            <Webhook className="w-4 h-4" />
            Integrations
          </TabsTrigger>
          <TabsTrigger value="leakage" className="flex items-center gap-2 relative">
            <AlertOctagon className="w-4 h-4" />
            Leakage Alerts
            {leakageAlerts?.summary?.total_at_risk > 0 && (
              <Badge className="ml-1 bg-red-500 text-white text-xs">{leakageAlerts.summary.total_at_risk}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="lcd" className="flex items-center gap-2">
            <FileSearch className="w-4 h-4" />
            LCD Extraction
          </TabsTrigger>
          <TabsTrigger value="compliance" className="flex items-center gap-2">
            <Shield className="w-4 h-4" />
            Compliance
          </TabsTrigger>
          <TabsTrigger value="poc" className="flex items-center gap-2">
            <Clipboard className="w-4 h-4" />
            Plan of Care
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab - UNIFIED SINGLE SOURCE OF TRUTH */}
        <TabsContent value="overview" className="space-y-6">
          {/* Single Source of Truth Banner */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Shield className="w-8 h-8" />
              <div>
                <h3 className="font-bold text-lg">Admission Dashboard - Single Source of Truth</h3>
                <p className="text-sm text-blue-100">All referrals regardless of source flow through this unified pipeline</p>
              </div>
            </div>
            <Badge className="bg-white/20 text-white border-0">
              {unifiedDashboard?.is_single_source_of_truth ? 'VERIFIED' : 'ACTIVE'}
            </Badge>
          </div>

          {/* 7-Stage Pipeline */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Activity className="w-5 h-5 text-blue-600" />
                Unified Pipeline Stages
              </CardTitle>
              <CardDescription>End-to-end workflow from referral received to Start of Care</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-7 gap-2">
                {[
                  { id: 'new', label: 'New', color: 'bg-blue-100 border-blue-500 text-blue-700' },
                  { id: 'under_review', label: 'Under Review', color: 'bg-amber-100 border-amber-500 text-amber-700' },
                  { id: 'pending_documentation', label: 'Pending Docs', color: 'bg-orange-100 border-orange-500 text-orange-700' },
                  { id: 'pending_cti', label: 'Pending CTI', color: 'bg-purple-100 border-purple-500 text-purple-700' },
                  { id: 'approved', label: 'Approved', color: 'bg-teal-100 border-teal-500 text-teal-700' },
                  { id: 'scheduled_soc', label: 'Scheduled SOC', color: 'bg-cyan-100 border-cyan-500 text-cyan-700' },
                  { id: 'admitted', label: 'Admitted', color: 'bg-green-100 border-green-500 text-green-700' }
                ].map((stage, idx) => (
                  <div key={stage.id} className="relative">
                    <div className={`text-center p-3 rounded-lg border-t-4 ${stage.color}`}>
                      <p className="text-xs font-medium">{stage.label}</p>
                      <p className="text-2xl font-bold">{unifiedDashboard?.pipeline_stats?.[stage.id] || 0}</p>
                    </div>
                    {idx < 6 && (
                      <ChevronRight className="absolute -right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-300" />
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Conversion Metrics */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Target className="w-5 h-5 text-green-600" />
                  Conversion Metrics
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="text-slate-600">Conversion Rate</span>
                      <span className="font-bold text-green-600">
                        {unifiedDashboard?.conversion_rate || dashboard?.conversion?.admission_rate || 0}%
                      </span>
                    </div>
                    <Progress 
                      value={unifiedDashboard?.conversion_rate || dashboard?.conversion?.admission_rate || 0} 
                      className="h-3"
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-4 pt-4 border-t">
                    <div className="text-center p-3 bg-emerald-50 rounded-lg">
                      <p className="text-2xl font-bold text-emerald-600">
                        {unifiedDashboard?.summary?.admitted || dashboard?.conversion?.total_admitted || 0}
                      </p>
                      <p className="text-xs text-slate-600">Admitted</p>
                    </div>
                    <div className="text-center p-3 bg-rose-50 rounded-lg">
                      <p className="text-2xl font-bold text-rose-600">
                        {unifiedDashboard?.summary?.not_admitted || dashboard?.conversion?.total_not_admitted || 0}
                      </p>
                      <p className="text-xs text-slate-600">Not Admitted</p>
                    </div>
                    <div className="text-center p-3 bg-blue-50 rounded-lg">
                      <p className="text-2xl font-bold text-blue-600">
                        {unifiedDashboard?.summary?.in_process || 0}
                      </p>
                      <p className="text-xs text-slate-600">In Process</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Key Performance Indicators */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                  Key Performance Indicators
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <Timer className="w-4 h-4 text-blue-500" />
                      <span className="text-sm text-slate-600">Avg Days to Admit</span>
                    </div>
                    <p className="text-2xl font-bold text-blue-700">
                      {unifiedDashboard?.avg_days_to_admit || 2.3}
                    </p>
                  </div>
                  <div className="p-3 bg-green-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <Calendar className="w-4 h-4 text-green-500" />
                      <span className="text-sm text-slate-600">Weekly Admits</span>
                    </div>
                    <p className="text-2xl font-bold text-green-700">
                      {unifiedDashboard?.weekly_admits || 12}
                    </p>
                  </div>
                  <div className="p-3 bg-amber-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <AlertTriangle className="w-4 h-4 text-amber-500" />
                      <span className="text-sm text-slate-600">Aging (&gt;48h)</span>
                    </div>
                    <p className="text-2xl font-bold text-amber-700">
                      {unifiedDashboard?.aging_referrals || 0}
                    </p>
                  </div>
                  <div className="p-3 bg-purple-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <UserPlus className="w-4 h-4 text-purple-500" />
                      <span className="text-sm text-slate-600">Today&apos;s Admits</span>
                    </div>
                    <p className="text-2xl font-bold text-purple-700">
                      {unifiedDashboard?.summary?.admitted_today || 0}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Referral Sources */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-indigo-600" />
                  Referral Sources (All Unified)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(unifiedDashboard?.by_source || dashboard?.referral_sources || {}).map(([source, count]) => (
                    <div key={source} className="flex items-center justify-between">
                      <span className="text-sm capitalize">{source.replace(/_/g, ' ')}</span>
                      <div className="flex items-center gap-2">
                        <div className="w-32 bg-slate-100 rounded-full h-2">
                          <div 
                            className="bg-indigo-500 rounded-full h-2"
                            style={{ 
                              width: `${(count / (unifiedDashboard?.summary?.total_referrals || dashboard?.summary?.total_referrals || 1)) * 100}%` 
                            }}
                          />
                        </div>
                        <span className="font-medium text-sm w-8 text-right">{count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Non-Admit Reasons */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <XCircle className="w-5 h-5 text-rose-600" />
                  Non-Admit Reasons
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(dashboard?.non_admit_reasons || {}).map(([reason, count]) => (
                    <div key={reason} className="flex items-center justify-between p-2 bg-rose-50 rounded">
                      <span className="text-sm capitalize">{reason.replace(/_/g, ' ')}</span>
                      <Badge variant="secondary">{count}</Badge>
                    </div>
                  ))}
                  {Object.keys(dashboard?.non_admit_reasons || {}).length === 0 && (
                    <p className="text-sm text-slate-500 text-center py-4">No non-admits recorded</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Pipeline Tab */}
        <TabsContent value="pipeline" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Pipeline Stages */}
            {[
              { key: 'pending_eligibility', label: 'Pending Eligibility', icon: Clock, color: 'amber' },
              { key: 'eligible', label: 'Eligible', icon: CheckCircle, color: 'green' },
              { key: 'pending_decision', label: 'Pending Decision', icon: Users, color: 'purple' },
              { key: 'in_admission', label: 'In Admission', icon: Activity, color: 'indigo' }
            ].map(stage => (
              <Card key={stage.key} className={`border-t-4 border-${stage.color}-500`}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <stage.icon className={`w-4 h-4 text-${stage.color}-600`} />
                      {stage.label}
                    </div>
                    <Badge className={`bg-${stage.color}-100 text-${stage.color}-700`}>
                      {pipeline?.[stage.key]?.count || 0}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {(pipeline?.[stage.key]?.referrals || []).map(ref => (
                      <div 
                        key={ref.id} 
                        className="p-2 bg-slate-50 rounded text-xs cursor-pointer hover:bg-slate-100"
                        onClick={() => openReferralDetails(ref)}
                      >
                        <div className="font-medium">{ref.patient?.full_name || ref.id}</div>
                        <div className="text-slate-500 flex items-center gap-1 mt-1">
                          <Building2 className="w-3 h-3" />
                          {ref.referral_source}
                          {ref.urgency === 'urgent' && (
                            <Badge className="ml-1 bg-amber-500 text-[10px] px-1">URGENT</Badge>
                          )}
                          {ref.urgency === 'emergent' && (
                            <Badge className="ml-1 bg-red-500 text-[10px] px-1">EMERGENT</Badge>
                          )}
                        </div>
                      </div>
                    ))}
                    {(pipeline?.[stage.key]?.referrals || []).length === 0 && (
                      <p className="text-xs text-slate-400 text-center py-4">No referrals</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Workflow Stages Visual */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Admission Workflow Stages</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between overflow-x-auto pb-4">
                {[
                  { label: 'Referral Intake', icon: FileText },
                  { label: 'AI Eligibility', icon: Brain },
                  { label: 'Decision', icon: Users },
                  { label: 'VOA & Orders', icon: ClipboardCheck },
                  { label: 'CTI', icon: Stethoscope },
                  { label: 'Consents', icon: FileText },
                  { label: 'Admission Visit', icon: Heart },
                  { label: 'Start of Care', icon: CheckCircle }
                ].map((stage, idx) => (
                  <React.Fragment key={stage.label}>
                    <div className="flex flex-col items-center min-w-[100px]">
                      <div className={`w-12 h-12 rounded-full ${idx < 3 ? 'bg-emerald-100' : 'bg-slate-100'} flex items-center justify-center mb-2`}>
                        <stage.icon className={`w-6 h-6 ${idx < 3 ? 'text-emerald-600' : 'text-slate-400'}`} />
                      </div>
                      <span className="text-xs text-center font-medium">{stage.label}</span>
                    </div>
                    {idx < 7 && (
                      <ChevronRight className="w-5 h-5 text-slate-300 flex-shrink-0" />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Documentation Monitor Tab - Per User Spec Section D */}
        <TabsContent value="documentation" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" />
                Required Documentation Monitor
              </CardTitle>
              <CardDescription>
                Visual indicators (green/yellow/red) for missing or overdue items. Select a referral to view details.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Documentation Stats Overview */}
              <div className="grid grid-cols-4 gap-4 mb-6">
                <div className="text-center p-4 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-2xl font-bold text-green-700">
                    {unifiedDashboard?.documentation_stats?.election_statement || 0}
                  </p>
                  <p className="text-xs text-slate-600">Elections Signed</p>
                </div>
                <div className="text-center p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-2xl font-bold text-blue-700">
                    {unifiedDashboard?.documentation_stats?.orders_verbal || 0}
                  </p>
                  <p className="text-xs text-slate-600">Verbal Orders</p>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <p className="text-2xl font-bold text-purple-700">
                    {unifiedDashboard?.documentation_stats?.f2f_note || 0}
                  </p>
                  <p className="text-xs text-slate-600">F2F Notes</p>
                </div>
                <div className="text-center p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-2xl font-bold text-amber-700">
                    {unifiedDashboard?.documentation_stats?.consents_complete || 0}
                  </p>
                  <p className="text-xs text-slate-600">Consents Complete</p>
                </div>
              </div>

              {/* Select Referral for Doc Monitor */}
              {unifiedDashboard?.recent_referrals?.length > 0 ? (
                <div className="space-y-4">
                  <h4 className="font-medium text-sm">Select Referral for Documentation Details:</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {unifiedDashboard.recent_referrals.slice(0, 9).map(ref => (
                      <div 
                        key={ref.id} 
                        className={`p-3 border rounded-lg cursor-pointer transition-all ${
                          selectedReferral?.id === ref.id ? 'border-blue-500 bg-blue-50' : 'hover:border-gray-300'
                        }`}
                        onClick={() => {
                          setSelectedReferral(ref);
                          fetchDocMonitor(ref.id);
                        }}
                      >
                        <div className="font-medium text-sm">{ref.patient?.full_name || ref.id}</div>
                        <div className="text-xs text-slate-500 flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-[10px]">{ref.status}</Badge>
                          <span>{ref.referral_source}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-center text-slate-500 py-8">No active referrals to display</p>
              )}

              {/* Documentation Monitor Details */}
              {docMonitor && (
                <div className="mt-6 border-t pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-medium">Documentation Status: {docMonitor.patient_name}</h4>
                    <Badge variant="outline">
                      {docMonitor.summary?.completion_percentage || 0}% Complete
                    </Badge>
                  </div>
                  
                  <Progress 
                    value={docMonitor.summary?.completion_percentage || 0} 
                    className="h-2 mb-4"
                  />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {docMonitor.documentation_items?.map(item => (
                      <div 
                        key={item.id}
                        className={`p-3 rounded-lg border-l-4 ${
                          item.indicator === 'green' ? 'bg-green-50 border-green-500' :
                          item.indicator === 'red' ? 'bg-red-50 border-red-500' :
                          item.indicator === 'yellow' ? 'bg-yellow-50 border-yellow-500' :
                          'bg-gray-50 border-gray-300'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">{item.name}</span>
                          {item.complete ? (
                            <CheckCircle className="w-4 h-4 text-green-600" />
                          ) : item.indicator === 'red' ? (
                            <AlertTriangle className="w-4 h-4 text-red-600" />
                          ) : (
                            <Clock className="w-4 h-4 text-gray-400" />
                          )}
                        </div>
                        <div className="text-xs mt-1">
                          {item.complete ? (
                            <span className="text-green-600">Complete</span>
                          ) : item.indicator === 'red' ? (
                            <span className="text-red-600">Overdue by {item.hours_overdue}h</span>
                          ) : item.hours_remaining ? (
                            <span className="text-amber-600">{item.hours_remaining}h remaining</span>
                          ) : (
                            <span className="text-gray-500">Not started</span>
                          )}
                          {item.blocks_billing && !item.complete && (
                            <span className="ml-2 text-red-600 font-medium">• Blocks Billing</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {docMonitor.blockers?.length > 0 && (
                    <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex items-center gap-2 text-red-700 font-medium mb-2">
                        <AlertTriangle className="w-4 h-4" />
                        Admission Blockers
                      </div>
                      <ul className="text-sm text-red-600 list-disc list-inside">
                        {docMonitor.blockers.map((blocker, idx) => (
                          <li key={idx}>{blocker}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Analytics Tab - Per User Spec Section F */}
        <TabsContent value="analytics" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Conversion & Time Analytics */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                  Referral-to-Admission Analytics
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-600">Conversion Rate</span>
                      <span className="text-3xl font-bold text-green-700">
                        {reportingAnalytics?.conversion_rate || unifiedDashboard?.conversion_rate || 0}%
                      </span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-blue-50 rounded-lg">
                      <p className="text-xs text-slate-600">Avg Time to Admit</p>
                      <p className="text-xl font-bold text-blue-700">
                        {reportingAnalytics?.avg_time_to_admit_hours 
                          ? `${(reportingAnalytics.avg_time_to_admit_hours / 24).toFixed(1)} days`
                          : 'N/A'}
                      </p>
                    </div>
                    <div className="p-3 bg-purple-50 rounded-lg">
                      <p className="text-xs text-slate-600">In Process</p>
                      <p className="text-xl font-bold text-purple-700">
                        {reportingAnalytics?.summary?.in_process || unifiedDashboard?.summary?.in_process || 0}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Source Performance */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-indigo-600" />
                  Referral Source Performance
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(reportingAnalytics?.source_performance || {}).slice(0, 6).map(([source, data]) => (
                    <div key={source} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                      <span className="text-sm capitalize">{source.replace(/_/g, ' ')}</span>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-slate-500">{data.total} total</span>
                        <Badge className={data.conversion_rate >= 80 ? 'bg-green-100 text-green-700' : 
                                          data.conversion_rate >= 60 ? 'bg-amber-100 text-amber-700' : 
                                          'bg-red-100 text-red-700'}>
                          {data.conversion_rate}%
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Weekly Volume */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-cyan-600" />
                  Weekly Admissions Volume
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {(reportingAnalytics?.weekly_volume || []).map((week, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <span className="text-sm w-20">{week.week}</span>
                      <div className="flex-1 bg-slate-100 rounded-full h-4 overflow-hidden">
                        <div 
                          className="bg-cyan-500 h-full rounded-full"
                          style={{ width: `${(week.admitted / Math.max(week.referrals, 1)) * 100}%` }}
                        />
                      </div>
                      <span className="text-xs w-24 text-right">
                        {week.admitted}/{week.referrals}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Non-Admit Reasons */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <XCircle className="w-5 h-5 text-rose-600" />
                  Reasons for Non-Admission
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {Object.entries(reportingAnalytics?.non_admit_reasons || unifiedDashboard?.non_admit_reasons || {}).map(([reason, count]) => (
                    <div key={reason} className="flex items-center justify-between p-2 bg-rose-50 rounded">
                      <span className="text-sm capitalize">{reason.replace(/_/g, ' ')}</span>
                      <Badge variant="outline" className="border-rose-300 text-rose-700">{count}</Badge>
                    </div>
                  ))}
                  {Object.keys(reportingAnalytics?.non_admit_reasons || unifiedDashboard?.non_admit_reasons || {}).length === 0 && (
                    <p className="text-center text-slate-500 py-4">No non-admits recorded</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Aging Referrals Alert */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                  Aging Referrals Without Movement (&gt;48 hours)
                </CardTitle>
              </CardHeader>
              <CardContent>
                {reportingAnalytics?.aging_referrals?.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {reportingAnalytics.aging_referrals.map(ref => (
                      <div key={ref.referral_id} className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <div className="font-medium text-sm">{ref.patient_name}</div>
                        <div className="flex items-center justify-between mt-2">
                          <Badge variant="outline" className="text-[10px]">{ref.status}</Badge>
                          <span className="text-xs text-amber-700 font-medium">
                            {ref.hours_without_movement}h stale
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-2" />
                    <p className="text-slate-600">No aging referrals - all referrals are progressing normally</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Metrics Tab */}
        <TabsContent value="metrics" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-blue-600" />
                  Status Distribution
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(dashboard?.status_breakdown || {}).map(([status, count]) => (
                    <div key={status} className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full ${getStatusColor(status)}`} />
                      <span className="text-sm flex-1 capitalize">{formatStatusLabel(status)}</span>
                      <span className="font-medium">{count}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Zap className="w-5 h-5 text-amber-600" />
                  Pending Action Items
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                    <span className="text-sm">Eligibility Reviews</span>
                    <Badge variant="outline" className="border-amber-500 text-amber-700">
                      {dashboard?.pending_items?.eligibility_reviews || 0}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                    <span className="text-sm">Admission Decisions</span>
                    <Badge variant="outline" className="border-purple-500 text-purple-700">
                      {dashboard?.pending_items?.admission_decisions || 0}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-indigo-50 rounded-lg">
                    <span className="text-sm">In-Progress Admissions</span>
                    <Badge variant="outline" className="border-indigo-500 text-indigo-700">
                      {dashboard?.pending_items?.in_progress_admissions || 0}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* HOPE Assessment Tab */}
        <TabsContent value="hope" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* HOPE Assessment Form */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <ClipboardCheck className="w-5 h-5 text-blue-600" />
                  HOPE Assessment (HIS)
                </CardTitle>
                <CardDescription>
                  Hospice Outcomes & Patient Evaluation - CMS-compliant assessment
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Select Referral/Patient</Label>
                  <Select 
                    value={hopeForm.referral_id}
                    onValueChange={(v) => setHopeForm(prev => ({...prev, referral_id: v}))}
                  >
                    <SelectTrigger data-testid="hope-referral-select">
                      <SelectValue placeholder="Select a referral..." />
                    </SelectTrigger>
                    <SelectContent>
                      {(pipeline?.in_admission?.referrals || []).map(ref => (
                        <SelectItem key={ref.id} value={ref.id}>
                          {ref.patient?.full_name || ref.id}
                        </SelectItem>
                      ))}
                      {(pipeline?.admitted?.referrals || []).map(ref => (
                        <SelectItem key={ref.id} value={ref.id}>
                          {ref.patient?.full_name || ref.id} (Admitted)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Assessment Type</Label>
                    <Select 
                      value={hopeForm.assessment_type}
                      onValueChange={(v) => setHopeForm(prev => ({...prev, assessment_type: v}))}
                    >
                      <SelectTrigger data-testid="hope-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admission">Admission (SOC)</SelectItem>
                        <SelectItem value="discharge">Discharge</SelectItem>
                        <SelectItem value="transfer">Transfer</SelectItem>
                        <SelectItem value="death">Death</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Assessment Date</Label>
                    <Input 
                      type="date"
                      value={hopeForm.assessment_date}
                      onChange={(e) => setHopeForm(prev => ({...prev, assessment_date: e.target.value}))}
                      data-testid="hope-assessment-date"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Assessor Name <span className="text-red-500">*</span></Label>
                    <Input 
                      value={hopeForm.assessor_name}
                      onChange={(e) => setHopeForm(prev => ({...prev, assessor_name: e.target.value}))}
                      placeholder="RN Name"
                      data-testid="hope-assessor-name"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Assessor NPI</Label>
                    <Input 
                      value={hopeForm.assessor_npi}
                      onChange={(e) => setHopeForm(prev => ({...prev, assessor_npi: e.target.value}))}
                      placeholder="NPI Number"
                      data-testid="hope-assessor-npi"
                    />
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h4 className="font-medium mb-3">Symptom Assessment</h4>
                  
                  {/* Pain Severity */}
                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between">
                      <Label>Pain Severity (0-10)</Label>
                      <Badge className={hopeForm.pain_severity >= 7 ? 'bg-red-500' : hopeForm.pain_severity >= 4 ? 'bg-amber-500' : 'bg-green-500'}>
                        {hopeForm.pain_severity}
                      </Badge>
                    </div>
                    <Slider 
                      value={[hopeForm.pain_severity]}
                      onValueChange={([v]) => setHopeForm(prev => ({...prev, pain_severity: v}))}
                      max={10}
                      step={1}
                      className="w-full"
                      data-testid="hope-pain-slider"
                    />
                    <p className="text-xs text-slate-500">Score ≥5 triggers Symptom Follow-up Visit (SFV)</p>
                  </div>

                  {/* Dyspnea */}
                  <div className="space-y-2 mb-4">
                    <Label>Dyspnea Severity</Label>
                    <Select 
                      value={hopeForm.dyspnea_severity}
                      onValueChange={(v) => setHopeForm(prev => ({...prev, dyspnea_severity: v}))}
                    >
                      <SelectTrigger data-testid="hope-dyspnea-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">None</SelectItem>
                        <SelectItem value="1">Mild (with activity)</SelectItem>
                        <SelectItem value="2">Moderate (limits daily activity)</SelectItem>
                        <SelectItem value="3">Severe (at rest)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Nausea */}
                  <div className="space-y-2 mb-4">
                    <Label>Nausea Severity</Label>
                    <Select 
                      value={hopeForm.nausea_severity}
                      onValueChange={(v) => setHopeForm(prev => ({...prev, nausea_severity: v}))}
                    >
                      <SelectTrigger data-testid="hope-nausea-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">None</SelectItem>
                        <SelectItem value="1">Mild (occasional)</SelectItem>
                        <SelectItem value="2">Moderate (frequent)</SelectItem>
                        <SelectItem value="3">Severe (constant)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Anxiety */}
                  <div className="space-y-2 mb-4">
                    <Label>Anxiety Level</Label>
                    <Select 
                      value={hopeForm.anxiety_severity}
                      onValueChange={(v) => setHopeForm(prev => ({...prev, anxiety_severity: v}))}
                    >
                      <SelectTrigger data-testid="hope-anxiety-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">None</SelectItem>
                        <SelectItem value="1">Mild</SelectItem>
                        <SelectItem value="2">Moderate (triggers SFV)</SelectItem>
                        <SelectItem value="3">Severe (triggers SFV)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Sadness */}
                  <div className="space-y-2">
                    <Label>Sadness/Depression</Label>
                    <Select 
                      value={hopeForm.sadness_severity}
                      onValueChange={(v) => setHopeForm(prev => ({...prev, sadness_severity: v}))}
                    >
                      <SelectTrigger data-testid="hope-sadness-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">None</SelectItem>
                        <SelectItem value="1">Mild</SelectItem>
                        <SelectItem value="2">Moderate (triggers SFV)</SelectItem>
                        <SelectItem value="3">Severe (triggers SFV)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="border-t pt-4 space-y-2">
                  <Label>Additional Notes</Label>
                  <Textarea 
                    value={hopeForm.notes}
                    onChange={(e) => setHopeForm(prev => ({...prev, notes: e.target.value}))}
                    placeholder="Clinical observations, family concerns, etc."
                    className="min-h-[80px]"
                    data-testid="hope-notes"
                  />
                </div>

                <Button 
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  onClick={handleHopeAssessment}
                  disabled={hopeLoading || !hopeForm.referral_id || !hopeForm.assessor_name}
                  data-testid="hope-submit-btn"
                >
                  {hopeLoading ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <ClipboardCheck className="w-4 h-4 mr-2" />
                  )}
                  Complete HOPE Assessment
                </Button>
              </CardContent>
            </Card>

            {/* HOPE Results / SFV Status */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Stethoscope className="w-5 h-5 text-purple-600" />
                  Assessment Results & SFV
                </CardTitle>
                <CardDescription>
                  Symptom Follow-up Visit (SFV) auto-triggers for moderate/severe symptoms
                </CardDescription>
              </CardHeader>
              <CardContent>
                {hopeResult ? (
                  <div className="space-y-4">
                    {/* SFV Alert */}
                    {hopeResult.sfv_triggered && (
                      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                        <div className="flex items-center gap-2 text-red-700 font-semibold mb-2">
                          <AlertTriangle className="w-5 h-5" />
                          SFV Triggered
                        </div>
                        <p className="text-sm text-red-600 mb-2">
                          Symptom Follow-up Visit required within 48 hours
                        </p>
                        <div className="space-y-1">
                          {hopeResult.sfv_symptoms?.map((s, idx) => (
                            <div key={idx} className="flex items-center gap-2 text-sm">
                              <Badge className="bg-red-500">{s.symptom}</Badge>
                              <span className="text-red-600">{s.severity}</span>
                            </div>
                          ))}
                        </div>
                        {hopeResult.sfv_order && (
                          <div className="mt-3 p-2 bg-white rounded border">
                            <p className="text-xs font-medium">SFV Order Created</p>
                            <p className="text-xs text-slate-600">ID: {hopeResult.sfv_order.id}</p>
                            <p className="text-xs text-slate-600">Due: {new Date(hopeResult.sfv_order.due_date).toLocaleDateString()}</p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Assessment Summary */}
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <h4 className="font-medium mb-2">Assessment Summary</h4>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>HOPE ID: <span className="font-mono">{hopeResult.hope_assessment_id}</span></div>
                        <div>Type: <Badge variant="outline">{hopeResult.assessment_type}</Badge></div>
                      </div>
                    </div>

                    {/* CMS Export */}
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        className="flex-1"
                        onClick={() => exportHopeToCMS(hopeResult.hope_assessment_id)}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Export CMS HOPE
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={() => {
                          navigator.clipboard.writeText(JSON.stringify(hopeResult, null, 2));
                          toast.success('HOPE data copied to clipboard');
                        }}
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <ClipboardCheck className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">Complete a HOPE assessment to see results</p>
                    <p className="text-sm text-slate-400 mt-2">
                      SFV will auto-trigger if symptoms are moderate to severe
                    </p>
                  </div>
                )}

                {/* SFV Info Box */}
                <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                  <h4 className="font-medium text-blue-800 mb-2">SFV Auto-Trigger Criteria</h4>
                  <ul className="text-xs text-blue-700 space-y-1">
                    <li>• Pain score ≥ 5/10</li>
                    <li>• Dyspnea level ≥ Moderate</li>
                    <li>• Nausea level ≥ Moderate</li>
                    <li>• Anxiety level ≥ Moderate</li>
                    <li>• Sadness/Depression ≥ Moderate</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Post-Admission Timeline Tab */}
        <TabsContent value="timeline" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Patient Selector */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users className="w-5 h-5 text-indigo-600" />
                  Admitted Patients
                </CardTitle>
                <CardDescription>
                  Select a patient to view their post-admission timeline
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {(pipeline?.admitted?.referrals || []).length > 0 ? (
                    (pipeline?.admitted?.referrals || []).map(ref => (
                      <div 
                        key={ref.id}
                        className={`p-3 rounded-lg cursor-pointer border transition-colors ${
                          selectedTimeline?.referral_id === ref.id 
                            ? 'bg-indigo-50 border-indigo-300' 
                            : 'bg-slate-50 border-slate-200 hover:bg-slate-100'
                        }`}
                        onClick={() => fetchAdmissionTimeline(ref.id)}
                      >
                        <div className="font-medium">{ref.patient?.full_name || ref.id}</div>
                        <div className="text-xs text-slate-500 mt-1">
                          Admitted: {ref.created_at ? new Date(ref.created_at).toLocaleDateString() : 'N/A'}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8">
                      <Users className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                      <p className="text-sm text-slate-500">No admitted patients</p>
                    </div>
                  )}
                  
                  {/* Also show in-admission patients */}
                  {(pipeline?.in_admission?.referrals || []).length > 0 && (
                    <>
                      <div className="border-t my-3 pt-3">
                        <p className="text-xs font-medium text-slate-500 mb-2">In Admission Process</p>
                      </div>
                      {(pipeline?.in_admission?.referrals || []).map(ref => (
                        <div 
                          key={ref.id}
                          className={`p-3 rounded-lg cursor-pointer border transition-colors ${
                            selectedTimeline?.referral_id === ref.id 
                              ? 'bg-amber-50 border-amber-300' 
                              : 'bg-slate-50 border-slate-200 hover:bg-slate-100'
                          }`}
                          onClick={() => fetchAdmissionTimeline(ref.id)}
                        >
                          <div className="font-medium">{ref.patient?.full_name || ref.id}</div>
                          <Badge className="bg-amber-500 text-xs mt-1">In Progress</Badge>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Timeline View */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-lg flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CalendarClock className="w-5 h-5 text-emerald-600" />
                    Post-Admission Timeline
                  </div>
                  {selectedTimeline && (
                    <Badge className={
                      selectedTimeline.overall_progress >= 80 ? 'bg-green-500' :
                      selectedTimeline.overall_progress >= 50 ? 'bg-amber-500' : 'bg-red-500'
                    }>
                      {selectedTimeline.overall_progress}% Complete
                    </Badge>
                  )}
                </CardTitle>
                <CardDescription>
                  Track medication orders, frequency orders, and SOC milestones
                </CardDescription>
              </CardHeader>
              <CardContent>
                {timelineLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
                  </div>
                ) : selectedTimeline ? (
                  <div className="space-y-4">
                    {/* Patient Info */}
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <div className="font-medium">{selectedTimeline.patient_name}</div>
                      <div className="text-sm text-slate-600">
                        Admission Date: {new Date(selectedTimeline.admission_date).toLocaleDateString()}
                      </div>
                      <div className="text-sm text-slate-600">
                        Current Day: {selectedTimeline.current_day}
                      </div>
                    </div>

                    {/* Progress Bar */}
                    <Progress value={selectedTimeline.overall_progress} className="h-3" />

                    {/* Milestones */}
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {selectedTimeline.milestones?.map((milestone, idx) => (
                        <div 
                          key={milestone.id} 
                          className={`p-4 rounded-lg border ${
                            milestone.day <= selectedTimeline.current_day 
                              ? 'bg-white border-slate-200' 
                              : 'bg-slate-50 border-slate-100 opacity-75'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                                milestone.day < selectedTimeline.current_day ? 'bg-green-500' :
                                milestone.day === selectedTimeline.current_day ? 'bg-blue-500' : 'bg-slate-300'
                              }`}>
                                {milestone.day}
                              </div>
                              <div>
                                <div className="font-medium">{milestone.title}</div>
                                <div className="text-xs text-slate-500">
                                  Due: {new Date(milestone.due_date).toLocaleDateString()}
                                </div>
                              </div>
                            </div>
                            {milestone.day === selectedTimeline.current_day && (
                              <Badge className="bg-blue-500">Today</Badge>
                            )}
                          </div>

                          <div className="space-y-2">
                            {milestone.items?.map((item, itemIdx) => (
                              <div 
                                key={itemIdx}
                                className={`flex items-center justify-between p-2 rounded ${
                                  item.status === 'complete' ? 'bg-green-50' : 
                                  item.status === 'pending' && item.required ? 'bg-amber-50' : 'bg-slate-50'
                                }`}
                              >
                                <div className="flex items-center gap-2">
                                  {item.status === 'complete' ? (
                                    <CheckCircle className="w-4 h-4 text-green-600" />
                                  ) : item.required ? (
                                    <Clock className="w-4 h-4 text-amber-600" />
                                  ) : (
                                    <div className="w-4 h-4 rounded-full border-2 border-slate-300" />
                                  )}
                                  <span className="text-sm">{item.task}</span>
                                  {item.required && item.status !== 'complete' && (
                                    <Badge variant="outline" className="text-xs ml-2">Required</Badge>
                                  )}
                                </div>
                                {item.status !== 'complete' && (
                                  <Button 
                                    size="sm" 
                                    variant="ghost"
                                    onClick={() => updateTimelineItem(selectedTimeline.referral_id, milestone.id, item.task, 'complete')}
                                  >
                                    <CheckCircle className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <CalendarClock className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">Select a patient to view their admission timeline</p>
                    <p className="text-sm text-slate-400 mt-2">
                      Track medication orders, frequencies, and SOC milestones
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Integrations Tab */}
        <TabsContent value="integrations" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Integration Sources */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Webhook className="w-5 h-5 text-purple-600" />
                  Referral Sources
                </CardTitle>
                <CardDescription>
                  External referral source integrations (MOCKED)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {integrationSources.map(source => (
                    <div key={source.id} className={`p-4 rounded-lg border ${
                      source.status === 'connected' ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'
                    }`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-2xl">{source.icon}</span>
                          <span className="font-medium">{source.name}</span>
                        </div>
                        <Badge className={source.status === 'connected' ? 'bg-green-500' : 'bg-slate-400'}>
                          {source.status}
                        </Badge>
                      </div>
                      <div className="text-sm text-slate-600">
                        Referrals today: <span className="font-bold">{source.referrals_today}</span>
                      </div>
                      <div className="flex gap-2 mt-3">
                        {source.status === 'connected' ? (
                          <>
                            <Button size="sm" variant="outline" className="flex-1">
                              <RefreshCw className="w-3 h-3 mr-1" />
                              Sync
                            </Button>
                            <Button size="sm" variant="outline">
                              <Pause className="w-3 h-3" />
                            </Button>
                          </>
                        ) : (
                          <Button size="sm" className="flex-1 bg-purple-600 hover:bg-purple-700">
                            <Play className="w-3 h-3 mr-1" />
                            Connect
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-center gap-2 text-amber-700 font-medium mb-1">
                    <AlertTriangle className="w-4 h-4" />
                    Integration Status: MOCKED
                  </div>
                  <p className="text-xs text-amber-600">
                    Real integrations with Aidin, CarePort, and Epic require API credentials. 
                    Contact your account manager to configure.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Recent Integration Referrals */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <GitBranch className="w-5 h-5 text-blue-600" />
                  Integration Referral Feed
                </CardTitle>
                <CardDescription>
                  Recent referrals received from integrated sources
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Demo data for integration feed */}
                <div className="space-y-3">
                  {[
                    { id: 'INT-001', source: 'Aidin', patient: 'John Smith', facility: 'Memorial Hospital', time: '2 hours ago', status: 'pending' },
                    { id: 'INT-002', source: 'CarePort', patient: 'Mary Johnson', facility: 'Sunset SNF', time: '4 hours ago', status: 'processed' },
                    { id: 'INT-003', source: 'Aidin', patient: 'Robert Davis', facility: 'City Medical Center', time: '6 hours ago', status: 'processed' },
                    { id: 'INT-004', source: 'CarePort', patient: 'Susan Wilson', facility: 'Pine Grove ALF', time: '1 day ago', status: 'admitted' },
                    { id: 'INT-005', source: 'Aidin', patient: 'James Brown', facility: 'Regional Hospital', time: '1 day ago', status: 'processed' }
                  ].map(ref => (
                    <div key={ref.id} className="p-3 bg-slate-50 rounded-lg border flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          ref.source === 'Aidin' ? 'bg-blue-100 text-blue-600' : 
                          ref.source === 'CarePort' ? 'bg-purple-100 text-purple-600' : 'bg-green-100 text-green-600'
                        }`}>
                          <LinkIcon className="w-5 h-5" />
                        </div>
                        <div>
                          <div className="font-medium">{ref.patient}</div>
                          <div className="text-xs text-slate-500">{ref.facility}</div>
                          <div className="text-xs text-slate-400">{ref.source} • {ref.time}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={
                          ref.status === 'admitted' ? 'bg-green-500' :
                          ref.status === 'processed' ? 'bg-blue-500' : 'bg-amber-500'
                        }>
                          {ref.status}
                        </Badge>
                        <Button size="sm" variant="outline">
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Integration Stats */}
                <div className="mt-4 grid grid-cols-3 gap-4">
                  <div className="p-3 bg-blue-50 rounded-lg text-center">
                    <div className="text-2xl font-bold text-blue-600">8</div>
                    <div className="text-xs text-slate-600">Today</div>
                  </div>
                  <div className="p-3 bg-purple-50 rounded-lg text-center">
                    <div className="text-2xl font-bold text-purple-600">45</div>
                    <div className="text-xs text-slate-600">This Week</div>
                  </div>
                  <div className="p-3 bg-green-50 rounded-lg text-center">
                    <div className="text-2xl font-bold text-green-600">87%</div>
                    <div className="text-xs text-slate-600">Conversion</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Compliance Tab */}
        <TabsContent value="compliance" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Shield className="w-5 h-5 text-green-600" />
                  Compliance Rates
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm text-slate-600">CTI Completion Rate</span>
                    <span className="font-bold text-green-600">
                      {dashboard?.compliance?.cti_completion_rate || 0}%
                    </span>
                  </div>
                  <Progress 
                    value={dashboard?.compliance?.cti_completion_rate || 0} 
                    className="h-2"
                  />
                </div>
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm text-slate-600">Consent Completion Rate</span>
                    <span className="font-bold text-green-600">
                      {dashboard?.compliance?.consent_completion_rate || 0}%
                    </span>
                  </div>
                  <Progress 
                    value={dashboard?.compliance?.consent_completion_rate || 0} 
                    className="h-2"
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                  SLA Alerts
                </CardTitle>
              </CardHeader>
              <CardContent>
                {slaAlerts.length > 0 ? (
                  <div className="space-y-2">
                    {slaAlerts.map((alert, idx) => (
                      <div key={idx} className="p-3 bg-amber-50 rounded-lg">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">{alert.type?.replace(/_/g, ' ')}</span>
                          <Badge variant={alert.severity === 'critical' ? 'destructive' : 'secondary'}>
                            {alert.severity}
                          </Badge>
                        </div>
                        <p className="text-xs text-slate-600 mt-1">{alert.message}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-2" />
                    <p className="text-slate-600">No SLA breaches detected</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Plan of Care Tab */}
        <TabsContent value="poc" className="space-y-6">
          <AdmissionPlanOfCare 
            patientId={selectedReferral?.patient_id || ''}
            referralId={selectedReferral?.id || ''}
            onComplete={(result) => {
              toast.success('Plan of Care Created', {
                description: `${result.summary.interventions_generated} interventions auto-generated`
              });
              setActiveTab('overview');
              fetchDashboard();
            }}
          />
        </TabsContent>

        {/* Leakage Alerts Tab */}
        <TabsContent value="leakage" className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-red-100 text-xs">Critical Alerts</p>
                    <p className="text-2xl font-bold">{leakageAlerts?.summary?.critical || 0}</p>
                  </div>
                  <AlertOctagon className="w-8 h-8 text-red-200" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-amber-100 text-xs">High Priority</p>
                    <p className="text-2xl font-bold">{leakageAlerts?.summary?.high || 0}</p>
                  </div>
                  <AlertTriangle className="w-8 h-8 text-amber-200" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-orange-100 text-xs">Medium</p>
                    <p className="text-2xl font-bold">{leakageAlerts?.summary?.medium || 0}</p>
                  </div>
                  <Clock className="w-8 h-8 text-orange-200" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-rose-500 to-rose-600 text-white">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-rose-100 text-xs">Revenue at Risk</p>
                    <p className="text-xl font-bold">${((leakageAlerts?.summary?.estimated_revenue_at_risk || 0) / 1000).toFixed(0)}K</p>
                  </div>
                  <DollarSign className="w-8 h-8 text-rose-200" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Alerts List */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <AlertOctagon className="w-5 h-5 text-red-600" />
                At-Risk Referrals
              </CardTitle>
              <CardDescription>
                Referrals at risk of being lost - immediate action required
              </CardDescription>
            </CardHeader>
            <CardContent>
              {(leakageAlerts?.alerts || []).length > 0 ? (
                <div className="space-y-3">
                  {leakageAlerts.alerts.map((alert, idx) => (
                    <div key={idx} className={`p-4 rounded-lg border-l-4 ${
                      alert.risk_level === 'critical' ? 'bg-red-50 border-red-500' :
                      alert.risk_level === 'high' ? 'bg-amber-50 border-amber-500' :
                      'bg-orange-50 border-orange-500'
                    }`}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold">{alert.patient_name}</span>
                            <Badge className={
                              alert.risk_level === 'critical' ? 'bg-red-500' :
                              alert.risk_level === 'high' ? 'bg-amber-500' : 'bg-orange-500'
                            }>
                              {alert.risk_level}
                            </Badge>
                            <Badge variant="outline" className="text-xs">
                              {alert.reason_label}
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-600 mb-2">{alert.description}</p>
                          <div className="flex items-center gap-4 text-xs text-slate-500">
                            <span>Status: {alert.status?.replace(/_/g, ' ')}</span>
                            <span>At risk: {alert.hours_at_risk} hrs</span>
                            <span>Urgency: {alert.urgency_score}/100</span>
                          </div>
                          <div className="mt-2 p-2 bg-white rounded text-xs">
                            <strong>Recommended:</strong> {alert.recommended_action}
                          </div>
                        </div>
                        <div className="flex flex-col gap-2 ml-4">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => acknowledgeLeakageAlert(alert.referral_id, 'followed_up')}
                          >
                            <Phone className="w-3 h-3 mr-1" />
                            Called
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => openReferralDetails({id: alert.referral_id, patient: {full_name: alert.patient_name}})}
                          >
                            <Eye className="w-3 h-3 mr-1" />
                            View
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <p className="text-lg font-medium text-slate-700">No At-Risk Referrals</p>
                  <p className="text-sm text-slate-500">All referrals are progressing normally</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* LCD Extraction Tab */}
        <TabsContent value="lcd" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* LCD Categories Overview */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Stethoscope className="w-5 h-5 text-blue-600" />
                  LCD Categories (CMS L33393)
                </CardTitle>
                <CardDescription>
                  Local Coverage Determination criteria for hospice eligibility
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-2">
                  {lcdCategories.map((cat, idx) => (
                    <div key={idx} className="p-3 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer">
                      <p className="font-medium text-sm capitalize">{cat.category?.replace(/_/g, ' ')}</p>
                      <p className="text-xs text-slate-500">{cat.lcd_number}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* LCD Extraction Form */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileSearch className="w-5 h-5 text-purple-600" />
                  Extract LCD Criteria
                </CardTitle>
                <CardDescription>
                  Analyze hospital records to identify eligibility criteria
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Referral ID</Label>
                  <Input 
                    value={lcdForm.referral_id}
                    onChange={(e) => setLcdForm(prev => ({...prev, referral_id: e.target.value}))}
                    placeholder="REF-XXXXXXXX-XXXXXXXX"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Record Type</Label>
                  <Select 
                    value={lcdForm.record_type}
                    onValueChange={(v) => setLcdForm(prev => ({...prev, record_type: v}))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="discharge_summary">Discharge Summary</SelectItem>
                      <SelectItem value="progress_notes">Progress Notes</SelectItem>
                      <SelectItem value="lab_results">Lab Results</SelectItem>
                      <SelectItem value="imaging">Imaging Report</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Facility Name</Label>
                  <Input 
                    value={lcdForm.facility_name}
                    onChange={(e) => setLcdForm(prev => ({...prev, facility_name: e.target.value}))}
                    placeholder="Hospital/Facility name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Hospital Record Text</Label>
                  <Textarea 
                    value={lcdForm.record_text}
                    onChange={(e) => setLcdForm(prev => ({...prev, record_text: e.target.value}))}
                    placeholder="Paste discharge summary, progress notes, or other clinical documentation here..."
                    className="min-h-[150px] font-mono text-xs"
                  />
                </div>
                <Button 
                  className="w-full" 
                  onClick={handleLcdExtraction}
                  disabled={lcdLoading || !lcdForm.referral_id || !lcdForm.record_text}
                >
                  {lcdLoading ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Brain className="w-4 h-4 mr-2" />
                  )}
                  Extract LCD Criteria
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* LCD Extraction Result */}
          {lcdExtractionResult && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    LCD Extraction Results
                  </div>
                  <Badge className={
                    lcdExtractionResult.eligibility_confidence >= 70 ? 'bg-green-500' :
                    lcdExtractionResult.eligibility_confidence >= 40 ? 'bg-amber-500' : 'bg-red-500'
                  }>
                    {lcdExtractionResult.eligibility_confidence}% Confidence
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <p className="text-xs text-blue-600 font-medium">LCD Category</p>
                    <p className="font-semibold capitalize">{lcdExtractionResult.lcd_category?.replace(/_/g, ' ')}</p>
                  </div>
                  <div className="p-3 bg-purple-50 rounded-lg">
                    <p className="text-xs text-purple-600 font-medium">LCD Number</p>
                    <p className="font-semibold">{lcdExtractionResult.lcd_number}</p>
                  </div>
                </div>

                {/* Criteria Found */}
                <div>
                  <h4 className="font-medium text-green-700 mb-2 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Criteria Found ({lcdExtractionResult.criteria_found?.length || 0})
                  </h4>
                  <div className="space-y-2">
                    {(lcdExtractionResult.criteria_found || []).map((cf, idx) => (
                      <div key={idx} className="p-2 bg-green-50 rounded text-sm">
                        <p className="font-medium">{typeof cf === 'object' ? cf.criterion : cf}</p>
                        {cf.evidence && <p className="text-xs text-green-600 mt-1">{cf.evidence}</p>}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Criteria Missing */}
                {(lcdExtractionResult.criteria_missing || []).length > 0 && (
                  <div>
                    <h4 className="font-medium text-amber-700 mb-2 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      Criteria Not Found ({lcdExtractionResult.criteria_missing?.length || 0})
                    </h4>
                    <div className="space-y-1">
                      {lcdExtractionResult.criteria_missing.map((cm, idx) => (
                        <div key={idx} className="p-2 bg-amber-50 rounded text-sm text-amber-700">
                          {cm}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* AI Analysis */}
                {lcdExtractionResult.ai_analysis && (
                  <div className="p-3 bg-slate-50 rounded">
                    <p className="text-xs text-slate-500 font-medium mb-1">AI Analysis</p>
                    <p className="text-sm">{lcdExtractionResult.ai_analysis}</p>
                  </div>
                )}

                {/* Generate Writeup Button */}
                <div className="flex gap-3">
                  <Button 
                    onClick={() => handleGenerateWriteup(lcdExtractionResult.referral_id)}
                    disabled={lcdLoading}
                    className="flex-1"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Generate Physician Write-up
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Physician Writeup Result */}
          {physicianWriteup && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-purple-600" />
                    Physician Certification Write-up
                  </div>
                  <Badge className={
                    physicianWriteup.certification_recommendation?.includes('recommended') 
                      ? 'bg-green-500' : 'bg-amber-500'
                  }>
                    {physicianWriteup.certification_recommendation}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-slate-50 p-4 rounded font-mono text-xs whitespace-pre-wrap max-h-96 overflow-y-auto">
                  {physicianWriteup.writeup_text}
                </div>
                <div className="flex gap-3">
                  <Button 
                    variant="outline"
                    onClick={() => {
                      navigator.clipboard.writeText(physicianWriteup.writeup_text);
                      toast.success('Write-up copied to clipboard');
                    }}
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copy
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => window.print()}
                  >
                    <Printer className="w-4 h-4 mr-2" />
                    Print
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Demo indicator */}
      {dashboard?.is_demo && (
        <div className="mt-4 text-center">
          <Badge variant="outline" className="text-xs">
            Demo Data - Create referrals to see real data
          </Badge>
        </div>
      )}

      {/* Referral Details Dialog */}
      <Dialog open={showReferralDetails} onOpenChange={setShowReferralDetails}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Brain className="w-5 h-5 text-indigo-600" />
              AI-Powered Referral Analysis
            </DialogTitle>
          </DialogHeader>
          
          {detailsLoading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Patient Header */}
              <div className="bg-slate-50 p-4 rounded-lg">
                <h3 className="font-semibold text-lg">{selectedReferral?.patient?.full_name || selectedReferral?.id}</h3>
                <div className="flex items-center gap-4 mt-2 text-sm text-slate-600">
                  <span>DOB: {selectedReferral?.patient?.dob}</span>
                  <span>Source: {selectedReferral?.referral_source}</span>
                  <Badge className={getStatusColor(selectedReferral?.status)}>
                    {formatStatusLabel(selectedReferral?.status)}
                  </Badge>
                </div>
              </div>

              {/* Conversion Score */}
              {conversionScore && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Target className="w-4 h-4 text-green-600" />
                      Predictive Conversion Score
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-4">
                      <div className={`w-20 h-20 rounded-full flex items-center justify-center text-2xl font-bold text-white ${
                        conversionScore.risk_color === 'green' ? 'bg-green-500' :
                        conversionScore.risk_color === 'amber' ? 'bg-amber-500' :
                        conversionScore.risk_color === 'orange' ? 'bg-orange-500' : 'bg-red-500'
                      }`}>
                        {conversionScore.conversion_score}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium capitalize">{conversionScore.risk_level?.replace(/_/g, ' ')}</p>
                        <p className="text-sm text-slate-600">{conversionScore.recommendation}</p>
                      </div>
                    </div>
                    
                    {/* Score Factors */}
                    <div className="mt-4 space-y-2">
                      <p className="text-sm font-medium text-slate-700">Score Factors:</p>
                      {Object.entries(conversionScore.factors || {}).slice(0, 4).map(([key, factor]) => (
                        <div key={key} className="flex items-center justify-between text-xs">
                          <span className="text-slate-600 capitalize">{key.replace(/_/g, ' ')}</span>
                          <div className="flex items-center gap-2">
                            <Progress value={(factor.score / factor.max_score) * 100} className="w-20 h-2" />
                            <span className="font-medium w-12 text-right">{factor.score}/{factor.max_score}</span>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Action Items */}
                    {conversionScore.action_items?.length > 0 && (
                      <div className="mt-4 p-3 bg-amber-50 rounded">
                        <p className="text-sm font-medium text-amber-800 mb-2">Action Items:</p>
                        {conversionScore.action_items.map((item, idx) => (
                          <div key={idx} className="text-xs text-amber-700 flex items-start gap-2 mb-1">
                            <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                            <span>{item.action}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Readiness Checklist */}
              {readinessChecklist && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <ClipboardCheck className="w-4 h-4 text-blue-600" />
                        Admission Readiness
                      </div>
                      <Badge className={
                        readinessChecklist.status_color === 'green' ? 'bg-green-500' :
                        readinessChecklist.status_color === 'amber' ? 'bg-amber-500' :
                        readinessChecklist.status_color === 'orange' ? 'bg-orange-500' : 'bg-red-500'
                      }>
                        {readinessChecklist.readiness_score}% Ready
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-slate-600 mb-4">{readinessChecklist.message}</p>
                    
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {Object.entries(readinessChecklist.categories || {}).map(([cat, data]) => (
                        <div key={cat} className="p-2 bg-slate-50 rounded">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-medium capitalize">{cat.replace(/_/g, ' ')}</span>
                            <span className="text-xs text-slate-500">{data.complete}/{data.total}</span>
                          </div>
                          <Progress value={data.completion_pct} className="h-1.5" />
                        </div>
                      ))}
                    </div>

                    {/* Next Steps */}
                    {readinessChecklist.next_steps?.length > 0 && (
                      <div className="mt-4">
                        <p className="text-sm font-medium text-slate-700 mb-2">Next Steps:</p>
                        {readinessChecklist.next_steps.slice(0, 3).map((step, idx) => (
                          <div key={idx} className={`text-xs flex items-start gap-2 mb-2 p-2 rounded ${
                            step.priority === 'critical' ? 'bg-red-50 text-red-700' : 'bg-slate-50 text-slate-600'
                          }`}>
                            <ChevronRight className="w-3 h-3 mt-0.5 flex-shrink-0" />
                            <div>
                              <span className="font-medium">{step.action}</span>
                              <span className="text-xs ml-2 opacity-75">({step.category})</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Physician Summary */}
              {physicianSummary && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-purple-600" />
                        Physician Certification Summary
                      </div>
                      {physicianSummary.certification_ready ? (
                        <Badge className="bg-green-500">Ready for Certification</Badge>
                      ) : (
                        <Badge className="bg-amber-500">Pending Items</Badge>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {/* Clinical Highlights */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {physicianSummary.clinical_highlights?.map((highlight, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs">
                          {highlight}
                        </Badge>
                      ))}
                    </div>

                    {/* Narrative Preview */}
                    <div className="bg-slate-50 p-3 rounded text-xs font-mono whitespace-pre-wrap max-h-48 overflow-y-auto">
                      {physicianSummary.narrative?.substring(0, 800)}...
                    </div>

                    {/* Missing Items */}
                    {physicianSummary.missing_for_certification?.length > 0 && (
                      <div className="mt-3 p-2 bg-amber-50 rounded">
                        <p className="text-xs font-medium text-amber-800">Missing for certification:</p>
                        <ul className="text-xs text-amber-700 mt-1 list-disc list-inside">
                          {physicianSummary.missing_for_certification.map((item, idx) => (
                            <li key={idx}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="mt-3"
                      onClick={() => {
                        navigator.clipboard.writeText(physicianSummary.narrative || '');
                        toast.success('Summary copied to clipboard');
                      }}
                    >
                      <FileText className="w-3 h-3 mr-2" />
                      Copy Full Summary
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReferralDetails(false)}>
              Close
            </Button>
            <Button onClick={() => handleRunEligibility(selectedReferral?.id)}>
              Run AI Eligibility
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdmissionsDashboard;
